import type { ReactNode } from 'react';
import { ArrowUp, Send, TrendingUp, UserRound } from 'lucide-react';
import type { EngagementFieldAvailability } from '@/api/generated/model';
import { Card, CardContent } from '@/components/ui/card';
import { ENGAGEMENT_LOCATION_HELP } from '../lib/engagementColors';
import { SectionLocationHint } from './EngagementChartFilters';
import { EngagementBusy, NotReadyBadge, NotReadyNote, engagementReadyState } from './EngagementNotReady';

export type ReferralView = {
  availability?: EngagementFieldAvailability;
  note?: string;
  successfulReferrals: number | null;
  invitations: number | null;
  invitationsAvailability?: EngagementFieldAvailability;
  conversionRate: number | null;
  conversionRateAvailability?: EngagementFieldAvailability;
};

/**
 * Figma "Referrals": hero count of successful referrals over two supporting
 * rows for invitations and conversion rate.
 */
export function ReferralsCard({ data, isLoading }: { data: ReferralView; isLoading?: boolean }) {
  const inviteState = engagementReadyState(data.invitationsAvailability);
  const conversionState = engagementReadyState(data.conversionRateAvailability);
  const conversionRising = (data.conversionRate ?? 0) >= 0;

  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardContent className="space-y-4 p-5">
        {isLoading ? (
          <EngagementBusy label="Loading referrals…" className="h-40" />
        ) : (
          <>
        <div className="flex items-center gap-3">
          <span className="flex size-9 items-center justify-center rounded-full bg-violet-500/15">
            <UserRound className="size-4 text-violet-400" aria-hidden />
          </span>
          <span className="text-base font-semibold">Referrals</span>
          <SectionLocationHint
            text={ENGAGEMENT_LOCATION_HELP.referrals}
            label={`How Referrals uses the location filter. ${ENGAGEMENT_LOCATION_HELP.referrals}`}
          />
        </div>

        <div className="text-center">
          <p className="text-[34px] font-bold leading-none tracking-tight tabular-nums text-violet-400">
            {(data.successfulReferrals ?? 0).toLocaleString()}
          </p>
          <p className="mt-1.5 text-xs text-muted-foreground">Successful Referrals</p>
        </div>

        <div className="divide-y divide-border/40 border-t border-border/40">
          <ReferralRow
            icon={<Send className="size-4 text-violet-400" aria-hidden />}
            tint="bg-violet-500/15"
            label="Invitations"
            state={inviteState}
            hint={data.note}
            value={
              inviteState === 'live' && data.invitations != null
                ? data.invitations.toLocaleString()
                : '—'
            }
          />
          <ReferralRow
            icon={<TrendingUp className="size-4 text-emerald-400" aria-hidden />}
            tint="bg-emerald-500/15"
            label="Conversion"
            state={conversionState}
            hint={data.note}
            value={
              conversionState === 'live' && data.conversionRate != null ? (
                <span className="inline-flex items-center gap-1">
                  <ArrowUp
                    className={
                      conversionRising ? 'size-3 text-emerald-400' : 'size-3 text-rose-400'
                    }
                    aria-hidden
                  />
                  {Math.abs(data.conversionRate).toFixed(1)}%
                </span>
              ) : (
                '—'
              )
            }
          />
        </div>

        {data.note ? <NotReadyNote>{data.note}</NotReadyNote> : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ReferralRow({
  icon,
  tint,
  label,
  value,
  state,
  hint,
}: {
  icon: ReactNode;
  tint: string;
  label: string;
  value: ReactNode;
  state: ReturnType<typeof engagementReadyState>;
  hint?: string;
}) {
  return (
    <div className="flex items-center gap-3 py-3">
      <span className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${tint}`}>
        {icon}
      </span>
      <span className="flex flex-1 items-center gap-1.5 text-sm">
        {label}
        <NotReadyBadge state={state} hint={hint} />
      </span>
      <span className="shrink-0 text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}
